import client from '../../lib/api';

// Yöneticinin en son hangi güncellemeye kadar okuduğu. Tercih sunucuya
// yazılmaz: "okundu" bilgisi kişiseldir, tarayıcıya aittir ve kaybolması
// yalnızca rozetin bir kez daha görünmesine yol açar.
const SEEN_KEY = 'admin:updates-seen-v1';

/** Sayfa listeyi okuduğunda yayınlanır; menü rozeti bunu dinler. */
export const UPDATES_SEEN_EVENT = 'bhs:admin-updates-seen';

const EMPTY = { entries: [], latest_date: '' };

let cachedToken = null;
let updatesRequest = null;

const readSeen = () => {
  try {
    return localStorage.getItem(SEEN_KEY) || '';
  } catch (storageError) {
    // Gizli sekmede ya da site verisi kapalı tarayıcıda okuma hata verir.
    // O kurulumda rozet her açılışta görünür; listenin kendisi çalışır.
    return '';
  }
};

/** En son okunan gün; sayfa hangi kutuların açık geleceğini buna göre seçer. */
export const readSeenDate = () => readSeen();

/**
 * Kayıtları güne göre kutular.
 *
 * Dosyada **aynı güne birden çok kayıt** düşer (bir günde birkaç iş biter) ve
 * her biri kendi başlığını taşır. Liste bu yüzden kayıt kayıt değil gün gün
 * çizilir: yönetici "o gün ne değişti" diye bakar, aynı tarihin altı ayrı
 * kartta tekrar etmesi günü okunmaz yapıyordu (tarih React anahtarı olarak da
 * kullanılamaz, çakışırdı).
 *
 * Sıra korunur: gün ilk göründüğü yerde açılır, dosyadaki "en yeni en üstte"
 * düzeni bozulmaz.
 */
export const groupUpdatesByDay = (entries) => {
  const days = [];
  const byDate = new Map();
  (entries || []).forEach((entry) => {
    const date = String(entry?.date || '');
    let day = byDate.get(date);
    if (!day) {
      day = {
        date, entries: [], total: 0, counts: {},
      };
      byDate.set(date, day);
      days.push(day);
    }
    const changes = Array.isArray(entry?.changes) ? entry.changes : [];
    day.entries.push(entry);
    day.total += changes.length;
    changes.forEach((change) => {
      const kind = change?.kind || 'note';
      day.counts[kind] = (day.counts[kind] || 0) + 1;
    });
  });
  return days;
};

/**
 * Listeyi yetkili istekle bir kez alır ve oturum belleğinde tutar.
 *
 * Kılavuzdaki yaklaşımın aynısı: aynı token için ikinci istek atılmaz,
 * başarısız istek önbelleğe kilitlenmez (geçici ağ hatasından sonra bir
 * sonraki açılış yeniden denesin).
 */
export const loadAdminUpdates = () => {
  const token = localStorage.getItem('pusubet_token') || '';
  if (updatesRequest && cachedToken === token) return updatesRequest;

  cachedToken = token;
  const request = client
    .get('/admin/updates')
    .then(({ data }) => ({
      entries: Array.isArray(data?.entries) ? data.entries : [],
      latest_date: data?.latest_date || '',
    }))
    .catch((requestError) => {
      if (updatesRequest === request) updatesRequest = null;
      throw requestError;
    });
  updatesRequest = request;
  return request;
};

/**
 * Okunmamış madde sayısı.
 *
 * Ölçü **madde** sayısıdır, gün sayısı değil: yönetici "üç yeni şey var"
 * bilgisini arar, "iki gün eklendi" bilgisini değil. Hiç okunmamışsa
 * listenin tamamı yeni sayılır — sayfayı ilk kez fark etmesi böyle olur.
 */
export const unseenUpdateCount = (entries, seen = readSeen()) => (
  (entries || [])
    .filter((entry) => !seen || String(entry?.date || '') > seen)
    .reduce((total, entry) => total + (entry?.changes?.length || 0), 0)
);

/** Rozetteki yazı: iki haneli sayılar menü satırını taşırır. */
export const unseenBadgeLabel = (count) => (count > 9 ? '9+' : String(count));

/** Listeyi okundu işaretler ve rozeti dinleyen menüye haber verir. */
export const markUpdatesSeen = (latestDate) => {
  if (!latestDate) return;
  try {
    localStorage.setItem(SEEN_KEY, String(latestDate));
  } catch (storageError) {
    // Yazılamadıysa rozet kalır; kayıt tutulamaması bir arıza değildir.
  }
  window.dispatchEvent(new CustomEvent(UPDATES_SEEN_EVENT));
};

/** Panel açılırken rozeti hesaplamak için listeyi sessizce alır. */
export const preloadAdminUpdates = () => loadAdminUpdates().then(
  (data) => data,
  () => EMPTY,
);

export default loadAdminUpdates;
